import Report from "../Report";
import m, {Vnode, VnodeDOM} from "mithril";
import {loadAndRunJs} from "../../../model/utils";
import printilo_print from "../../../lib/printilo_print";
import ICustomFormSettings from "../../ICustomFormSettings";
import {TextboxItem} from "../../../model/items/textbox";
import {LabelItem} from "../../../model/items/labelItem";
import LabelCell from "../../shared/components/FormCell/LabelCell";

export default class ReportPdf extends Report {

    private report?: string;
    private extendReport?: string;
    private pdfUrl?: string;
    private extendReportPdfUrl?: string;
    private readonly pdfUrlTextboxItem: TextboxItem;
    private readonly extendedReportPdfUrlTextboxItem?: TextboxItem;
    private readonly afterReportLabelCell?: LabelCell;

    private failed = false;

    constructor(settings: ICustomFormSettings) {
        super(settings);
        // Report
        this.pdfUrlTextboxItem = <TextboxItem>settings.form.grid?.layout[0][1].cellItems[0];
        this.pdfUrl = this.pdfUrlTextboxItem?.getVariable();

        // Extended report
        this.extendedReportPdfUrlTextboxItem = <TextboxItem>settings.form.grid?.layout[2]?.[1]?.cellItems[0];
        this.extendReportPdfUrl = this.extendedReportPdfUrlTextboxItem?.getVariable();

        // Label
        const afterReportLabel = <LabelItem>settings.form.grid?.layout[1][1].cellItems[0];
        if (afterReportLabel) {
            this.afterReportLabelCell = new LabelCell(afterReportLabel);
        }
    }

    checkRequirements(): boolean {
        if (!this.settings.form.aux1) {
            console.warn('customForm-Report-pdf: Report template missing.');
            return false;
        }
        if (!this.pdfUrlTextboxItem) {
            console.warn('customForm-Report-pdf: Textbox on position [0,1] for saving pdf url missing.');
            return false;
        }

        return true;
    }

    oncreate(vnode: VnodeDOM) {
        super.oncreate(vnode);
        this.loadReportTemplate();
    }

    private loadReportTemplate(): void {
        this.failed = false;
        loadAndRunJs(this.settings.form.aux1!, () => {
            this.renderReportTemplate();
        }, () => {
            this.failed = true;
            m.redraw();
        });

        setTimeout(() => {
            if (!this.report) {
                this.failed = true;
                m.redraw();
            }
        }, 15000);
    }

    private renderReportTemplate(): void {
        try {
            // @ts-ignore
            this.report = window.renderReport?.(this.settings.form.questionary);
            // @ts-ignore
            this.extendReport = window.extendedRenderExport?.(this.settings.form.questionary);
        } catch (e) {
            this.failed = true;
            console.error(e);
        }
        m.redraw();

        if (!this.pdfUrl && this.report) {
            printilo_print({
                html: this.report,
                onSuccess: (url) => this.onPrintSuccess(url),
                printer: "puppeteer",
            });
        }
        if (!this.extendReportPdfUrl && this.extendedReportPdfUrlTextboxItem && this.extendReport) {
            printilo_print({
                html: this.extendReport,
                onSuccess: (url) => this.onExtendedReportPrintSuccess(url),
                printer: "puppeteer",
            });
        }
    }

    private onPrintSuccess(url: string): void {
        this.pdfUrl = url;
        this.pdfUrlTextboxItem.setVariable(url);
        this.settings.form.questionary.save(); // save pdf link
        m.redraw();
    }

    private onExtendedReportPrintSuccess(url: string): void {
        this.extendReportPdfUrl = url;
        this.extendedReportPdfUrlTextboxItem?.setVariable(url);
        this.settings.form.questionary.save();
        m.redraw();
    }


    view(): Vnode {

        let content;
        if (this.failed) {
            content = [this.viewFailed()];
        } else if (!this.pdfUrl || !this.report) {
            content = [this.viewLoading()];
        } else {
            content = [
                m('.top-download-section', {}, this.viewDownloadButton()),
                m('iframe', {
                    oncreate: (vnode) => {
                        const iframe = <HTMLIFrameElement>vnode.dom;
                        iframe.contentWindow!.document.open();
                        iframe.contentWindow!.document.write(this.report!);
                        iframe.contentWindow!.document.close();
                        iframe.style.height = $(iframe.contentWindow!.document).outerHeight() + 'px';
                        iframe.contentWindow!.window.addEventListener('load', () => {
                            iframe.style.height = $(iframe.contentWindow!.document).innerHeight() + 'px';
                        });
                        iframe.contentWindow!.window.addEventListener('resize', () => {
                            iframe.style.height = $(iframe.contentWindow!.document).find('html').height() + 'px';
                        });
                    },
                }),
                m('.bottom-download-section', {}, this.viewDownloadButton()),
                this.afterReportLabelCell ? m(this.afterReportLabelCell) : null,
            ];
        }

        return this.viewLayout(content);
    }

    private viewLoading(): Vnode {
        const messages = this.settings.form.questionary.messages;
        return m('.loading', {}, [
            m('.loader', {}, [
                m('.loader-spinner'),
                m('.loader-message', {}, m.trust(messages.get('generatingReport'))),
            ])
        ]);
    }

    private viewFailed(): Vnode|null {
        const messages = this.settings.form.questionary.messages;
        return m('.text-center', {}, [
            m('p', {}, m.trust(messages.get('generatingReportError'))),
            m('button.btn.secondary', {
                onclick: () => this.loadReportTemplate(),
            }, m.trust(messages.get('tryAgain'))),
        ]);
    }

    private viewDownloadButton(): Vnode|null {
        const messages = this.settings.form.questionary.messages;
        return m('a.btn.success', {
            href: this.pdfUrl,
            download: 'Report.pdf',
            target: '_blank',
        }, [
            m('i.icon.icon-download.text-light'),
            m('span', {}, m.trust(messages.get('download')))
        ]);
    }

}
